import { apiAxiosAuth, apiAxiosCMS } from 'services/api';
import { BannerPopupProps } from 'types/BannerPopupProps';
import { SelectInputItemProps } from 'types/SelectInputItemProps';

type BannerPopupPayload = Omit<
	BannerPopupProps,
	| 'id'
	| 'createdAt'
	| 'updatedAt'
	| 'desktopImageUrl'
	| 'mobileImageUrl'
	| 'ctaLabel'
>;

export interface BannerPopupCustomerSegment {
	distributorId?: string;
}

interface VtexCategory {
	id: number;
	name: string;
	children?: VtexCategory[];
}

function normalizeBannerPopup(banner: any): BannerPopupProps {
	const type =
		banner.type === 'EVENT'
			? 'COUNTDOWN'
			: banner.type === 'OFFER'
				? 'SIMPLE'
				: banner.type;

	return {
		...banner,
		type,
		id: banner.id || '',
		rne: banner.rne || '',
		imageUrl:
			banner.imageUrl || banner.desktopImageUrl || banner.mobileImageUrl || '',
		countdownImages: banner.countdownImages || [],
		ctas:
			banner.ctas ||
			(banner.ctaLabel
				? [{ label: banner.ctaLabel, redirectUrl: banner.redirectUrl || '' }]
				: []),
		distributorIds: banner.distributorIds || [],
		createdAt: banner.createdAt || banner.created_at || '',
		updatedAt: banner.updatedAt || banner.updated_at || ''
	};
}

function normalizeBannerPopupList(data: any): BannerPopupProps[] {
	const list = Array.isArray(data) ? data : data?.items || data?.data || [];

	return list.map(normalizeBannerPopup);
}

function getBannerPopupPayload(form: BannerPopupProps): BannerPopupPayload {
	return {
		name: form.name,
		rne: form.rne || '',
		type: form.type,
		title: form.title,
		description: form.description || '',
		imageUrl: form.imageUrl || '',
		countdownImages: form.countdownImages || [],
		redirectUrl: form.redirectUrl || '',
		ctas: form.ctas || [],
		startDate: form.startDate,
		endDate: form.endDate,
		status: form.status,
		distributorIds: form.distributorIds || []
	};
}

function flattenCategories(
	categories: VtexCategory[],
	parentName = ''
): SelectInputItemProps[] {
	return categories.flatMap((category) => {
		const option = parentName
			? `${parentName} > ${category.name}`
			: category.name;
		return [
			{ id: String(category.id), option },
			...flattenCategories(category.children || [], option)
		];
	});
}

export function isBannerPopupInsideValidity(
	banner: BannerPopupProps,
	referenceDate = new Date()
) {
	const startDateValue = banner.startDate.includes('T')
		? banner.startDate
		: `${banner.startDate}T00:00:00`;
	const endDateValue = banner.endDate.includes('T')
		? banner.endDate
		: `${banner.endDate}T23:59:59`;
	const startDate = new Date(startDateValue);
	const endDate = new Date(endDateValue);

	return referenceDate >= startDate && referenceDate <= endDate;
}

export function isBannerPopupSegmentAllowed(
	banner: BannerPopupProps,
	customer: BannerPopupCustomerSegment = {}
) {
	const hasDistributorRule = (banner.distributorIds?.length || 0) > 0;

	if (
		hasDistributorRule &&
		!banner.distributorIds?.includes(customer.distributorId || '')
	) {
		return false;
	}

	return true;
}

export function canShowBannerPopupOnLogin(
	banner: BannerPopupProps,
	customer: BannerPopupCustomerSegment = {},
	referenceDate = new Date()
) {
	return (
		banner.status === 'ACTIVE' &&
		isBannerPopupInsideValidity(banner, referenceDate) &&
		isBannerPopupSegmentAllowed(banner, customer)
	);
}

export async function getBannerPopups(handleNotify: any) {
	try {
		const { data } = await apiAxiosCMS.get('/banner-popup');

		return { isSuccess: true, list: normalizeBannerPopupList(data) };
	} catch (error) {
		console.error('Erro ao buscar os Banner PopUps:', error);
		handleNotify('Erro ao buscar os Banner PopUps.', 'error');
		return { isSuccess: false, list: [] };
	}
}

export async function getBannerPopupById(id: string, handleNotify: any) {
	try {
		const { data } = await apiAxiosCMS.get(`/banner-popup/${id}`);

		return { isSuccess: true, banner: normalizeBannerPopup(data) };
	} catch (error) {
		console.error('Erro ao buscar o Banner PopUp:', error);
		handleNotify('Erro ao buscar o Banner PopUp.', 'error');
		return { isSuccess: false, banner: null };
	}
}

export async function getBannerPopupCategories(handleNotify: any) {
	try {
		const { data } = await apiAxiosAuth.get(
			'bridge/vtex/api/catalog_system/pub/category/tree/4'
		);

		return {
			isSuccess: true,
			list: flattenCategories(data || [])
		};
	} catch {
		handleNotify('Erro ao buscar as categorias.', 'error');
		return { isSuccess: false, list: [] };
	}
}

export async function addBannerPopupReq(
	form: BannerPopupProps,
	setIsLoad: React.Dispatch<React.SetStateAction<boolean>>,
	setMessage: React.Dispatch<React.SetStateAction<string>>,
	handleNotify: any
) {
	setMessage('Salvando Banner PopUp...');
	setIsLoad(true);

	try {
		const { data } = await apiAxiosCMS.post(
			'/banner-popup',
			getBannerPopupPayload(form)
		);
		const bannerSaved = normalizeBannerPopup(data);

		handleNotify('Banner PopUp adicionado com sucesso.', 'success');
		return { isSuccess: true, bannerSaved };
	} catch (error) {
		console.error('Erro ao adicionar Banner PopUp:', error);
		handleNotify('Erro ao adicionar Banner PopUp.', 'error');
		return { isSuccess: false, bannerSaved: null };
	} finally {
		setIsLoad(false);
	}
}

export async function editBannerPopupReq(
	bannerToEdit: BannerPopupProps,
	form: BannerPopupProps,
	setIsLoad: React.Dispatch<React.SetStateAction<boolean>>,
	setMessage: React.Dispatch<React.SetStateAction<string>>,
	handleNotify: any
) {
	setMessage('Editando Banner PopUp...');
	setIsLoad(true);

	try {
		await apiAxiosCMS.patch(
			`/banner-popup/${bannerToEdit.id}`,
			getBannerPopupPayload(form)
		);
		handleNotify('Banner PopUp editado com sucesso.', 'success');
		return { isSuccess: true };
	} catch (error) {
		console.error('Erro ao editar Banner PopUp:', error);
		handleNotify('Erro ao editar Banner PopUp.', 'error');
		return { isSuccess: false };
	} finally {
		setIsLoad(false);
	}
}

export async function deleteBannerPopupReq(
	id: string,
	setIsLoad: React.Dispatch<React.SetStateAction<boolean>>,
	setMessage: React.Dispatch<React.SetStateAction<string>>,
	handleNotify: any
) {
	setMessage('Deletando Banner PopUp...');
	setIsLoad(true);

	try {
		await apiAxiosCMS.delete(`/banner-popup/${id}`);
		handleNotify('Banner PopUp deletado com sucesso.', 'success');
		return { isSuccess: true };
	} catch (error) {
		console.error('Erro ao deletar Banner PopUp:', error);
		handleNotify('Erro ao deletar Banner PopUp.', 'error');
		return { isSuccess: false };
	} finally {
		setIsLoad(false);
	}
}
